import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { keyframes } from '@emotion/react';
import PageWithTitle from '../../components/atom/PageWithTitle';
import EventFilters, { DEFAULT_FILTER } from '../../components/parking/EventFilters';
import { applyFilter, bewebItem, catColor, clock } from '../../components/parking/eventItems';
import {
    CATEGORIES,
    LIVE_HOURS,
    fetchArchiveDay,
    fetchArchiveDays,
} from '../../helpers/parkingEvents';
import useLiveEvents from './useLiveEvents';
import Timeline, { COLOR_FAIL, COLOR_IN, COLOR_OUT, hourLabel } from './Timeline';
import PlateDrawer, { DRAWER_WIDTH } from './PlateDrawer';
import { matchPlate } from './plateMatch';

const MAX_ROWS = 500;
const MIN_SCORE = 0.6;
const LIVE = 'live';
const flash = keyframes`from { background-color: rgba(255, 213, 0, 0.35); } to { background-color: transparent; }`;

function Sum({ value, color, label, onClick, active }) {
    return (
        <Stack
            direction="row"
            spacing={0.75}
            alignItems="baseline"
            onClick={onClick}
            sx={{
                cursor: onClick ? 'pointer' : 'default',
                borderBottom: active ? `2px solid ${color}` : '2px solid transparent',
            }}
        >
            <Typography variant="h6">{value}</Typography>
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color }} />
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
        </Stack>
    );
}

// one archived day (from the PHP API), in the same Map shape as the live events
function useArchiveDay(date) {
    const [state, setState] = useState({ events: new Map(), loading: false, error: null });
    useEffect(() => {
        if (date === LIVE) return undefined;
        let stop = false;
        setState({ events: new Map(), loading: true, error: null });
        fetchArchiveDay(date)
            .then((list) => {
                if (stop) return;
                const events = new Map();
                list.forEach((e) => events.set(e.k, e));
                setState({ events, loading: false, error: null });
            })
            .catch((error) => !stop && setState({ events: new Map(), loading: false, error }));
        return () => {
            stop = true;
        };
    }, [date]);
    return state;
}

// most frequent problems of the shown range: by type and by plate, both clickable
function ProblemsPanel({ items, onType, onPlate }) {
    const problems = items.filter((i) => i.sev !== 'info');
    const top = (keyFn) => {
        const m = new Map();
        problems.forEach((i) => {
            const k = keyFn(i);
            if (!k) return;
            const cur = m.get(k) || { k, n: 0, fail: 0, cat: i.cat };
            cur.n += 1;
            if (i.sev === 'fail') cur.fail += 1;
            m.set(k, cur);
        });
        return [...m.values()].sort((a, b) => b.fail - a.fail || b.n - a.n).slice(0, 12);
    };
    const types = top((i) => i.label);
    const plates = top((i) => i.plate);
    if (!problems.length) return null;
    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Leggyakoribb hibák / figyelmeztetések
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {types.map((t) => (
                            <Chip
                                key={t.k}
                                size="small"
                                label={`${t.k} · ${t.n}`}
                                onClick={() => onType(t.k)}
                                variant={t.fail ? 'filled' : 'outlined'}
                                sx={
                                    t.fail
                                        ? { bgcolor: catColor(t.cat), color: '#fff' }
                                        : { borderColor: catColor(t.cat) }
                                }
                            />
                        ))}
                    </Stack>
                </Grid>
                <Grid item xs={12} md={5}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Rendszámok a legtöbb hibával
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {plates.map((p) => (
                            <Chip
                                key={p.k}
                                size="small"
                                variant="outlined"
                                label={`${p.k} · ${p.n}`}
                                onClick={() => onPlate(p.k)}
                                sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                            />
                        ))}
                    </Stack>
                </Grid>
            </Grid>
        </Paper>
    );
}

const rowBg = (theme, e) => {
    if (e.sev === 'fail') return alpha(theme.palette.error.main, 0.07);
    if (e.sev === 'warn') return alpha(theme.palette.warning.main, 0.07);
    return undefined;
};

const LiveParkingPage = () => {
    const live = useLiveEvents();
    const navigate = useNavigate();
    // opened from a parking: ?plate=ABC123 and ?hours=24|48|72 or ?day=YYYY-MM-DD
    const [searchParams] = useSearchParams();
    const [day, setDay] = useState(() =>
        /^\d{4}-\d{2}-\d{2}$/.test(searchParams.get('day') || '') ? searchParams.get('day') : LIVE
    );
    const [days, setDays] = useState([]);
    const archive = useArchiveDay(day);
    const [hours, setHours] = useState(() =>
        LIVE_HOURS.includes(Number(searchParams.get('hours')))
            ? Number(searchParams.get('hours'))
            : LIVE_HOURS[0]
    );
    const [filter, setFilter] = useState(DEFAULT_FILTER);
    const [query, setQuery] = useState(searchParams.get('plate') || '');
    const [hour, setHour] = useState(null);
    const [selected, setSelected] = useState({ plate: null, eventId: null });

    useEffect(() => {
        fetchArchiveDays()
            .then((r) => setDays(r.days || []))
            .catch(() => setDays([]));
    }, []);

    const isLive = day === LIVE;

    // "Vissza" on the parking page returns to this view with the same search and range
    const openParking = (plate) => {
        const back = new URLSearchParams({ plate: query.trim() || plate });
        if (isLive) back.set('hours', hours);
        else back.set('day', day);
        navigate(`/parkings/details/${encodeURIComponent(plate)}`, {
            state: { history: [`/liveparking?${back}`] },
        });
    };
    const { events, fresh } = isLive ? live : { events: archive.events, fresh: new Set() };
    const dayStart = isLive ? null : new Date(`${day}T00:00:00`).getTime();
    const rangeHours = isLive ? hours : 24;

    const items = useMemo(() => [...events.values()].map((e) => bewebItem(e)), [events]);

    // range + filters + fuzzy plate search; the chart shows this, the table additionally the clicked hour
    const list = useMemo(() => {
        const min = isLive ? Date.now() - hours * 3600e3 : dayStart;
        const max = isLive ? Infinity : dayStart + 24 * 3600e3;
        const q = query.trim();
        const out = [];
        applyFilter(items, filter).forEach((e) => {
            if (e.ts < min || e.ts >= max) return;
            const m = q ? matchPlate(q, e.plate) : { score: 1, rank: 1 };
            if (m.score >= MIN_SCORE) out.push({ ...e, ...m });
        });
        return out.sort((a, b) => (q ? b.rank - a.rank : 0) || b.ts - a.ts);
    }, [items, filter, hours, query, isLive, dayStart]);

    const inRange = useMemo(() => {
        const min = isLive ? Date.now() - hours * 3600e3 : dayStart;
        return items.filter((e) => e.ts >= min && (isLive || e.ts < dayStart + 24 * 3600e3));
    }, [items, hours, isLive, dayStart]);

    const rows = hour == null ? list : list.filter((e) => e.ts >= hour && e.ts < hour + 3600e3);
    const sumIn = list.filter((e) => e.dir === 'in').length;
    const sumOut = list.filter((e) => e.dir === 'out').length;
    const sumFail = list.filter((e) => e.sev === 'fail').length;
    const searching = !!query.trim();

    let statusText = 'Betöltés…';
    if (!isLive) statusText = archive.loading ? 'Archívum betöltése…' : `archívum · ${day}`;
    else if (live.error) statusText = null;
    else if (live.status && !live.status.connected) statusText = null;
    else if (live.status?.loading)
        statusText = `Előzmények betöltése… ${live.status.loadedFrom || ''}`;
    else if (live.updatedAt)
        statusText = `frissítve ${live.updatedAt.toLocaleTimeString('hu-HU')} · 10 mp-enként`;

    return (
        <Box
            sx={{ mr: selected.plate ? { sm: `${DRAWER_WIDTH}px` } : 0, transition: 'margin 0.2s' }}
        >
            <PageWithTitle
                title="Élő forgalom"
                headline={
                    <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
                        {statusText}
                    </Typography>
                }
            >
                {isLive && live.error && (
                    <Grid item xs={12}>
                        <Alert severity="error">
                            A parkoló proxy nem elérhető: {live.error.message}
                        </Alert>
                    </Grid>
                )}
                {isLive && !live.error && live.status && !live.status.connected && (
                    <Grid item xs={12}>
                        <Alert severity="warning">
                            A parkoló rendszer (be.web) nem elérhető: {live.status.error}
                        </Alert>
                    </Grid>
                )}
                {!isLive && archive.error && (
                    <Grid item xs={12}>
                        <Alert severity="error">
                            Az archívum nem érhető el: {archive.error.message}
                        </Alert>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                            <Stack
                                direction="row"
                                spacing={1.5}
                                flexWrap="wrap"
                                useFlexGap
                                alignItems="center"
                            >
                                <TextField
                                    select
                                    size="small"
                                    label="Időszak"
                                    value={day}
                                    onChange={(e) => {
                                        setDay(e.target.value);
                                        setHour(null);
                                    }}
                                    sx={{ minWidth: 200 }}
                                >
                                    <MenuItem value={LIVE}>Élő (utolsó 72 óra)</MenuItem>
                                    {!isLive && !days.some((d) => d.date === day) && (
                                        <MenuItem value={day}>{day}</MenuItem>
                                    )}
                                    {days.map((d) => (
                                        <MenuItem key={d.date} value={d.date}>
                                            {d.date}
                                            {d.closed ? '' : ' (folyamatban)'}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                {isLive && (
                                    <ToggleButtonGroup
                                        size="small"
                                        exclusive
                                        value={hours}
                                        onChange={(_, v) => v && setHours(v)}
                                    >
                                        {LIVE_HOURS.map((h) => (
                                            <ToggleButton key={h} value={h}>
                                                {h} óra
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                )}
                                <TextField
                                    size="small"
                                    placeholder="Rendszám keresés (hibatűrő)"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    sx={{ minWidth: 260 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {archive.loading && <CircularProgress size={20} />}
                            </Stack>
                            <EventFilters items={inRange} filter={filter} onChange={setFilter} />
                        </Stack>
                    </Paper>
                </Grid>

                <Grid item xs={12}>
                    <ProblemsPanel
                        items={inRange}
                        onType={(t) => setFilter({ ...filter, types: [t] })}
                        onPlate={(p) => setQuery(p)}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack
                            direction="row"
                            spacing={3}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mb: 1 }}
                        >
                            <Sum value={sumIn} color={COLOR_IN} label="behajtás" />
                            <Sum value={sumOut} color={COLOR_OUT} label="kihajtás" />
                            <Sum
                                value={sumFail}
                                color={COLOR_FAIL}
                                label="hiba"
                                active={filter.problemsOnly}
                                onClick={() =>
                                    setFilter({ ...filter, problemsOnly: !filter.problemsOnly })
                                }
                            />
                            <Typography variant="body2" color="text.secondary">
                                óránként, {isLive ? `utolsó ${hours} óra` : day}
                                {searching ? ' · keresésre szűrve' : ''}
                            </Typography>
                            {hour != null && (
                                <Chip
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    label={`Szűrve: ${hourLabel(hour)}`}
                                    onDelete={() => setHour(null)}
                                />
                            )}
                        </Stack>
                        <Timeline
                            list={list}
                            hours={rangeHours}
                            start={dayStart}
                            selectedHour={hour}
                            onSelectHour={setHour}
                        />
                    </Paper>
                </Grid>

                <Grid item xs={12}>
                    <Paper variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {searching && <TableCell>Egyezés</TableCell>}
                                    <TableCell>Idő</TableCell>
                                    <TableCell>Rendszám / jegy</TableCell>
                                    <TableCell>Esemény</TableCell>
                                    <TableCell>Kapu</TableCell>
                                    <TableCell>Részletek</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.slice(0, MAX_ROWS).map((e) => (
                                    <TableRow
                                        key={e.k}
                                        hover
                                        selected={e.k === selected.eventId}
                                        onClick={() =>
                                            e.plate && setSelected({ plate: e.plate, eventId: e.k })
                                        }
                                        sx={(theme) => ({
                                            cursor: e.plate ? 'pointer' : 'default',
                                            bgcolor: rowBg(theme, e),
                                            // 100% opacity at a 100% match, 50% at 0%
                                            opacity: searching ? 0.5 + 0.5 * e.score : 1,
                                            animation: fresh.has(e.k)
                                                ? `${flash} 3s ease-out`
                                                : 'none',
                                        })}
                                    >
                                        {searching && (
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={`${Math.round(e.score * 100)}%`}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {isLive
                                                ? e.time || clock(e.ts, true)
                                                : clock(e.ts, true)}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontFamily: 'monospace',
                                                fontWeight: 700,
                                                fontSize: 15,
                                                letterSpacing: 0.5,
                                            }}
                                        >
                                            {e.plate || '–'}
                                            {e.plate && (
                                                <IconButton
                                                    size="small"
                                                    title="Parkolás megnyitása"
                                                    onClick={(ev) => {
                                                        ev.stopPropagation();
                                                        openParking(e.plate);
                                                    }}
                                                    sx={{ ml: 0.5 }}
                                                >
                                                    <OpenInNewIcon fontSize="inherit" />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Box
                                                    title={
                                                        (CATEGORIES[e.cat] || CATEGORIES.other)
                                                            .label
                                                    }
                                                    sx={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: e.sev === 'fail' ? 0 : '50%',
                                                        transform:
                                                            e.sev === 'fail'
                                                                ? 'rotate(45deg)'
                                                                : 'none',
                                                        bgcolor: catColor(e.cat),
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: e.sev === 'info' ? 400 : 700,
                                                    }}
                                                    color={
                                                        e.sev === 'fail'
                                                            ? 'error.main'
                                                            : 'text.primary'
                                                    }
                                                >
                                                    {e.label}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {e.gateLabel}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {[
                                                    e.amount
                                                        ? `${new Intl.NumberFormat('hu-HU').format(
                                                              e.amount
                                                          )} Ft`
                                                        : '',
                                                    e.user,
                                                    e.detail,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            {rows.length > MAX_ROWS
                                ? `${MAX_ROWS} látható a ${rows.length} találatból – szűkíts kereséssel, szűrőkkel vagy órára kattintva.`
                                : `${rows.length} találat`}
                        </Typography>
                    </Paper>
                </Grid>
            </PageWithTitle>

            <PlateDrawer
                plate={selected.plate}
                eventId={selected.eventId}
                events={events}
                onSelectEvent={(k) => setSelected((s) => ({ ...s, eventId: k }))}
                onClose={() => setSelected({ plate: null, eventId: null })}
                onSearch={(q) => setQuery(q)}
                onOpenParking={openParking}
            />
        </Box>
    );
};

export default LiveParkingPage;
